import React, { useState, useRef, useEffect } from "react";
import { format } from "date-fns";
import Calendar from "react-calendar";
import tw from "tailwind-styled-components";
import { GoChevronDown } from "react-icons/go";
import { FaRegCalendarAlt } from "react-icons/fa";
import { InputGroup, Label } from "../Styles/InputStyles";
import moment from "moment";
import { IoClose } from "react-icons/io5";

const MonthFilter = ({ setFieldValue }) => {
    const popupRef = useRef(null);
    const [showPopup, setShowPopup] = useState(false);
    const [label, setLabel] = useState("This Month");
    const [selectedDate, setSelectedDate] = useState(new Date());

    useEffect(() => {
        const today = new Date();
        setLabel(format(today, "MMMM yyyy"));
        setSelectedDate(today);

        // For current month, start from month start and end at current date
        setFieldValue('startDate', moment(today).startOf('month').format("YYYY-MM-DD"));
        setFieldValue('endDate', moment(today).format("YYYY-MM-DD"));
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (popupRef.current && !popupRef.current.contains(e.target)) {
                setShowPopup(false);
            }
        };

        if (showPopup) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showPopup]);

    const handleDateChange = (date) => {
        const today = new Date();
        const selectedMonth = date.getMonth();
        const selectedYear = date.getFullYear();
        const currentMonth = today.getMonth();
        const currentYear = today.getFullYear();

        // Prevent future month selection
        if (selectedYear > currentYear || (selectedYear === currentYear && selectedMonth > currentMonth)) {
            return;
        }

        setSelectedDate(date);
        setLabel(format(date, "MMMM yyyy"));

        // Check if selected month is current month
        const isCurrentMonth = selectedYear === currentYear && selectedMonth === currentMonth;

        if (isCurrentMonth) {
            // For current month: start from month start, end at current date
            setFieldValue('startDate', moment(date).startOf('month').format("YYYY-MM-DD"));
            setFieldValue('endDate', moment(today).format("YYYY-MM-DD"));
        } else {
            // For past months: start and end at month boundaries
            setFieldValue('startDate', moment(date).startOf('month').format("YYYY-MM-DD"));
            setFieldValue('endDate', moment(date).endOf('month').format("YYYY-MM-DD"));
        }

        setShowPopup(false);
    };

    // Disable future months in calendar
    const tileDisabled = ({ date, view }) => {
        if (view === 'year') {
            const today = new Date();
            return date > today;
        }
        return false;
    };

    return (
        <div className="relative text-left h-12 text-gray-700 mb-6">
            <InputGroup>
                <Label>Select Month</Label>
                <Button type="button" onClick={() => setShowPopup(true)}>
                    <FaRegCalendarAlt className="h-4 w-4 mr-2 text-gray-500" />
                    <LabelText>{label}</LabelText>
                    <GoChevronDown className="h-5 w-5 ml-2 text-gray-500" />
                </Button>

                {showPopup && (
                    <Overlay>
                        <Popup ref={popupRef}>
                            <PopupHeader>
                                <PopupTitle>Select Month</PopupTitle>
                                <CloseButton type="button" onClick={() => setShowPopup(false)}>
                                    <IoClose className="h-6 w-6" />
                                </CloseButton>
                            </PopupHeader>
                            <PopupContent>
                                <CalendarWrapper>
                                    <Calendar
                                        onChange={handleDateChange}
                                        value={selectedDate}
                                        view="year"
                                        onClickMonth={(date) => handleDateChange(date)}
                                        maxDetail="year"
                                        minDetail="year"
                                        tileDisabled={tileDisabled}
                                        maxDate={new Date()}
                                    />
                                </CalendarWrapper>
                            </PopupContent>
                        </Popup>
                    </Overlay>
                )}
            </InputGroup>
        </div>
    );
};

// Styled Components
const Button = tw.button`
    w-full text-gray-600 inline-flex items-center px-3 py-2 border border-gray-300 
    shadow-sm text-sm rounded-md bg-white hover:bg-gray-50 h-16
`;
const LabelText = tw.span`truncate flex-1 text-left`;
const Overlay = tw.div`
    fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4
`;
const Popup = tw.div`
    bg-white rounded-xl shadow-xl w-full max-w-md h-auto max-h-[80vh] 
    flex flex-col
`;
const PopupHeader = tw.div`
    flex justify-between items-center px-6 py-4 border-b flex-shrink-0
`;
const PopupTitle = tw.h3`
    text-lg font-semibold text-gray-800
`;
const CloseButton = tw.button`
    text-gray-500 hover:text-gray-700 transition-colors duration-200
    p-1 rounded-full hover:bg-gray-100
`;
const PopupContent = tw.div`
    flex flex-col flex-1 p-6
`;
const CalendarWrapper = tw.div`
    [&_.react-calendar]:border-0 [&_.react-calendar]:w-full
    [&_.react-calendar__navigation]:flex [&_.react-calendar__navigation]:justify-between
    [&_.react-calendar__navigation]:mb-4
    [&_.react-calendar__navigation__label]:text-lg 
    [&_.react-calendar__navigation__label]:font-semibold
    [&_.react-calendar__navigation__arrow]:text-2xl
    [&_.react-calendar__navigation__arrow]:px-4
    [&_.react-calendar__tile]:text-sm [&_.react-calendar__tile]:p-3
    [&_.react-calendar__tile]:rounded-lg
    [&_.react-calendar__tile]:hover:bg-gray-100
    [&_.react-calendar__tile--active]:bg-primary-color 
    [&_.react-calendar__tile--active]:text-white
    [&_.react-calendar__tile--active]:rounded-lg
    [&_.react-calendar__year-view__months__month]:text-sm
    [&_.react-calendar__year-view__months__month]:p-4
    [&_.react-calendar__year-view__months__month]:rounded-lg
    [&_.react-calendar__year-view__months__month]:hover:bg-gray-100
    [&_.react-calendar__year-view__months__month--active]:bg-primary-color
    [&_.react-calendar__year-view__months__month--active]:text-white
    [&_.react-calendar__tile--disabled]:text-gray-300
    [&_.react-calendar__tile--disabled]:cursor-not-allowed
    [&_.react-calendar__tile--disabled]:hover:bg-transparent
`;

export default MonthFilter;